import { useCallback, useEffect, useState } from 'react'
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import CreateOrganizationDialog from './CreateOrganizationDialog'
import { invalidateOrganizationSettings } from '../hooks/useOrganizationSettings'
import {
  ACTIVE_ORG_CHANGED_EVENT,
  fetchUserOrganizations,
  getActiveOrganizationId,
  switchActiveOrganization,
  type UserOrganization,
} from '../utils/activeOrganization'

export function OrganizationPickerMenu({
  anchorEl,
  open,
  onClose,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  transformOrigin = { vertical: 'bottom', horizontal: 'left' },
}: {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' }
  transformOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' }
}) {
  const { checkAuth } = useAuth()
  const { showNotification } = useNotification()
  const [organizations, setOrganizations] = useState<UserOrganization[]>([])
  const [activeOrganizationId, setActiveOrganizationIdState] = useState<number | null>(null)
  const [switching, setSwitching] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  const loadOrganizations = useCallback(async () => {
    try {
      const data = await fetchUserOrganizations()
      setOrganizations(data.organizations ?? [])
      const activeId = data.active_organization_id || getActiveOrganizationId()
      if (activeId) {
        setActiveOrganizationIdState(activeId)
      }
    } catch {
      setOrganizations([])
    }
  }, [])

  useEffect(() => {
    if (open) void loadOrganizations()
  }, [open, loadOrganizations])

  useEffect(() => {
    const onOrgChanged = () => {
      const id = getActiveOrganizationId()
      setActiveOrganizationIdState(id)
    }
    window.addEventListener(ACTIVE_ORG_CHANGED_EVENT, onOrgChanged)
    return () => window.removeEventListener(ACTIVE_ORG_CHANGED_EVENT, onOrgChanged)
  }, [])

  const handleSwitch = async (organizationId: number) => {
    if (organizationId === activeOrganizationId) {
      onClose()
      return
    }
    setSwitching(true)
    try {
      await switchActiveOrganization(organizationId)
      setActiveOrganizationIdState(organizationId)
      invalidateOrganizationSettings()
      await checkAuth()
      showNotification('Switched organization', 'success')
      onClose()
    } catch {
      showNotification('Failed to switch organization', 'error')
    } finally {
      setSwitching(false)
    }
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        PaperProps={{
          sx: {
            bgcolor: 'var(--vs-surface)',
            border: 'var(--border)',
            borderRadius: 'var(--radius-md)',
            minWidth: 220,
            boxShadow: 'var(--shadow-overlay)',
            mb: 0.5,
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              borderRadius: 'var(--radius-sm)',
              mx: 0.5,
              px: 1.25,
              py: 0.75,
              color: 'var(--vs-text-secondary)',
              '& .MuiListItemIcon-root': { minWidth: 32, color: 'var(--vs-text-muted)' },
              '&:hover': { bgcolor: 'rgba(134,190,223,0.10)', color: 'var(--vs-text-primary)' },
            },
            '& .MuiDivider-root': { borderColor: 'var(--vs-border-subtle)', mx: 1 },
          },
        }}
      >
        {organizations.map((org) => (
          <MenuItem
            key={org.organization_id}
            selected={org.organization_id === activeOrganizationId}
            disabled={switching}
            onClick={() => void handleSwitch(org.organization_id)}
          >
            {org.name || `Organization ${org.organization_id}`}
          </MenuItem>
        ))}
        {organizations.length === 0 ? (
          <MenuItem disabled>No organizations</MenuItem>
        ) : null}
        <Divider />
        <MenuItem
          disabled={switching}
          onClick={() => {
            onClose()
            setCreateOpen(true)
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create organization…</ListItemText>
        </MenuItem>
      </Menu>
      <CreateOrganizationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void loadOrganizations()}
      />
    </>
  )
}
